import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
    FileText, 
    Target, 
    Brain, 
    CheckSquare, 
    ClipboardList, 
    AlertOctagon,
    CheckCircle2,
    AlertTriangle,
    RotateCcw,
    Sparkles,
    Lock,
    OctagonX
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';

// Post-Trade Review Checklist Data Structure
const postTradeChecklist = {
    maklumat_trade: {
        title: 'Maklumat Trade',
        title_en: 'Trade Information',
        icon: FileText,
        color: 'blue',
        items: [
            { id: 'mt_1', text: 'Entry, SL, dan TP direkod', text_en: 'Entry, SL, and TP recorded' },
            { id: 'mt_2', text: 'Pair / instrument betul', text_en: 'Pair / instrument correct' },
            { id: 'mt_3', text: 'Timeframe entry dicatat', text_en: 'Entry timeframe noted' }
        ]
    },
    pelaksanaan_trade: {
        title: 'Pelaksanaan Trade',
        title_en: 'Trade Execution',
        icon: Target,
        color: 'emerald',
        items: [
            { id: 'pt_1', text: 'Entry ikut plan asal', text_en: 'Entry followed original plan' },
            { id: 'pt_2', text: 'Tidak ubah SL/TP ikut emosi', text_en: 'Did not change SL/TP based on emotions' },
            { id: 'pt_3', text: 'Tidak overtrade dalam sesi ini', text_en: 'Did not overtrade in this session' }
        ]
    },
    psikologi_disiplin: {
        title: 'Psikologi & Disiplin',
        title_en: 'Psychology & Discipline',
        icon: Brain,
        color: 'purple',
        items: [
            { id: 'pd_1', text: 'Emosi terkawal sepanjang trade', text_en: 'Emotions controlled throughout trade' },
            { id: 'pd_2', text: 'Tidak revenge trade', text_en: 'No revenge trading' },
            { id: 'pd_3', text: 'Terima keputusan trade dengan tenang', text_en: 'Accepted trade outcome calmly' }
        ]
    },
    keputusan_trade: {
        title: 'Keputusan Trade',
        title_en: 'Trade Outcome',
        icon: CheckSquare,
        color: 'amber',
        items: [
            { id: 'kt_1', text: 'Trade ditutup ikut plan (TP / SL / manual valid)', text_en: 'Trade closed according to plan (TP / SL / valid manual)' },
            { id: 'kt_2', text: 'Result diterima tanpa penyesalan', text_en: 'Result accepted without regret' },
            { id: 'kt_3', text: 'Fokus pada proses, bukan hasil', text_en: 'Focus on process, not outcome' }
        ]
    },
    review_ringkas: {
        title: 'Review Ringkas',
        title_en: 'Quick Review',
        icon: ClipboardList,
        color: 'cyan',
        items: [
            { id: 'rr_1', text: 'Kenalpasti apa yang dibuat dengan betul', text_en: 'Identify what was done correctly' },
            { id: 'rr_2', text: 'Kenalpasti apa yang perlu diperbaiki', text_en: 'Identify what needs improvement' },
            { id: 'rr_3', text: 'Tetapkan action item untuk trade seterusnya', text_en: 'Set action items for next trade' }
        ]
    },
    final_rule: {
        title: 'Final Rule',
        title_en: 'Final Rule',
        icon: AlertOctagon,
        color: 'rose',
        items: [
            { id: 'fr_1', text: 'Jika capai max loss harian → STOP TRADE', text_en: 'If daily max loss reached → STOP TRADE', isWarning: true },
            { id: 'fr_2', text: 'Jika emosi terganggu → STOP TRADE', text_en: 'If emotions disturbed → STOP TRADE', isWarning: true }
        ]
    }
};

const colorStyles = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', ring: 'ring-blue-500/20' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', ring: 'ring-emerald-500/20' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', ring: 'ring-purple-500/20' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', ring: 'ring-amber-500/20' },
    cyan: { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-200', ring: 'ring-cyan-500/20' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', ring: 'ring-rose-500/20' }
};

export const PostTradeChecklist = ({ onComplete, className }) => {
    const { language } = useLanguage();
    
    // Initialize checkedItems from localStorage using lazy initialization
    const [checkedItems, setCheckedItems] = useState(() => {
        const saved = localStorage.getItem('postTradeChecklist');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const today = new Date().toDateString();
                if (parsed.date === today && parsed.items) {
                    return parsed.items;
                }
            } catch (e) {
                console.error('Error loading checklist:', e);
            }
        }
        return {};
    });
    const [showReminder, setShowReminder] = useState(false);

    // Calculate total items and checked count
    const totalItems = Object.values(postTradeChecklist).reduce((acc, section) => acc + section.items.length, 0);
    const checkedCount = Object.values(checkedItems).filter(Boolean).length;
    const isComplete = checkedCount === totalItems;
    const progress = Math.round((checkedCount / totalItems) * 100);

    // Ref to track if component has mounted
    const hasMounted = useRef(false);

    // Save to localStorage on change (skip initial mount)
    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        
        localStorage.setItem('postTradeChecklist', JSON.stringify({
            date: new Date().toDateString(),
            items: checkedItems
        }));
    }, [checkedItems]);

    // Derive daily loss warning from checkedItems (no useEffect needed)
    const dailyLossTriggered = checkedItems['fr_1'] || checkedItems['fr_2'];

    const handleToggle = (itemId) => {
        setCheckedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
        setShowReminder(false);
    };

    const handleReset = () => {
        setCheckedItems({});
        setShowReminder(false);
    };

    const handleProceed = () => {
        if (!isComplete) {
            setShowReminder(true);
            return;
        }
        if (onComplete) {
            onComplete();
        }
    };

    // Calculate section completion
    const getSectionProgress = (sectionKey) => {
        const section = postTradeChecklist[sectionKey];
        const sectionChecked = section.items.filter(item => checkedItems[item.id]).length;
        return {
            checked: sectionChecked,
            total: section.items.length,
            complete: sectionChecked === section.items.length
        };
    };

    return (
        <div className={cn("w-full max-w-3xl mx-auto", className)} data-testid="post-trade-checklist">
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                    Post-Trade Review
                </h2>
                <p className="text-slate-500 mt-2">
                    {language === 'id' 
                        ? 'Review disiplin dan pelaksanaan selepas setiap trade'
                        : 'Review discipline and execution after every trade'
                    }
                </p>
                
                {/* Progress Bar */}
                <div className="mt-6 flex items-center justify-center gap-4">
                    <div className="flex-1 max-w-xs h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                            className={cn(
                                "h-full rounded-full transition-all duration-500 ease-out",
                                isComplete ? "bg-emerald-500" : "bg-purple-500"
                            )}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className={cn(
                        "text-sm font-semibold",
                        isComplete ? "text-emerald-600" : "text-slate-600"
                    )}>
                        {checkedCount}/{totalItems}
                    </span>
                </div>
            </div>

            {/* Daily Loss Warning Banner */}
            {dailyLossTriggered && (
                <div 
                    className="mb-6 flex items-start gap-3 p-4 bg-red-50 border-2 border-red-300 rounded-xl animate-in fade-in"
                    data-testid="daily-loss-warning"
                >
                    <OctagonX className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-bold text-red-800">
                            {language === 'id' 
                                ? 'Had risiko harian dicapai. Hentikan sesi trade hari ini.'
                                : 'Daily risk limit reached. Stop trading session today.'
                            }
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                            {language === 'id'
                                ? 'Rehat dan kembali esok dengan mindset segar.'
                                : 'Rest and return tomorrow with a fresh mindset.'
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Checklist Sections */}
            <div className="space-y-4">
                {Object.entries(postTradeChecklist).map(([sectionKey, section]) => {
                    const Icon = section.icon;
                    const colors = colorStyles[section.color];
                    const sectionProgress = getSectionProgress(sectionKey);
                    const isWarningSection = sectionKey === 'final_rule';

                    return (
                        <div 
                            key={sectionKey}
                            className={cn(
                                "bg-white rounded-2xl border overflow-hidden transition-all duration-300",
                                sectionProgress.complete 
                                    ? isWarningSection
                                        ? "border-rose-200 shadow-sm shadow-rose-100"
                                        : "border-emerald-200 shadow-sm shadow-emerald-100" 
                                    : "border-slate-100"
                            )}
                            data-testid={`section-${sectionKey}`}
                        >
                            {/* Section Header */}
                            <div className={cn(
                                "flex items-center gap-3 px-5 py-4 border-b transition-colors",
                                sectionProgress.complete 
                                    ? isWarningSection
                                        ? "bg-rose-50/50 border-rose-100"
                                        : "bg-emerald-50/50 border-emerald-100" 
                                    : "bg-slate-50/50 border-slate-100"
                            )}>
                                <div className={cn(
                                    "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                                    sectionProgress.complete
                                        ? isWarningSection
                                            ? "bg-rose-500 text-white"
                                            : "bg-emerald-500 text-white"
                                        : `${colors.bg} ${colors.text}`
                                )}>
                                    {sectionProgress.complete 
                                        ? isWarningSection 
                                            ? <AlertOctagon className="w-5 h-5" />
                                            : <CheckCircle2 className="w-5 h-5" />
                                        : <Icon className="w-5 h-5" />
                                    }
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-slate-900">
                                        {language === 'en' ? section.title_en : section.title}
                                    </h3>
                                    <p className="text-xs text-slate-400">
                                        {sectionProgress.checked}/{sectionProgress.total} {language === 'id' ? 'selesai' : 'complete'}
                                    </p>
                                </div>
                                {sectionProgress.complete && (
                                    <span className={cn(
                                        "text-xs font-medium px-2 py-1 rounded-full",
                                        isWarningSection 
                                            ? "text-rose-600 bg-rose-100"
                                            : "text-emerald-600 bg-emerald-100"
                                    )}>
                                        ✓
                                    </span>
                                )}
                            </div>

                            {/* Section Items */}
                            <div className="divide-y divide-slate-50">
                                {section.items.map((item) => {
                                    const isChecked = checkedItems[item.id] || false;
                                    const isWarningItem = item.isWarning;

                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => handleToggle(item.id)}
                                            className={cn(
                                                "w-full flex items-center gap-4 px-5 py-4 text-left transition-all duration-200 hover:bg-slate-50/50",
                                                isChecked && (isWarningItem ? "bg-rose-50/30" : "bg-emerald-50/30")
                                            )}
                                            data-testid={`item-${item.id}`}
                                        >
                                            {/* Checkbox */}
                                            <div className={cn(
                                                "w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200",
                                                isChecked
                                                    ? isWarningItem
                                                        ? "bg-rose-500 border-rose-500 text-white"
                                                        : "bg-emerald-500 border-emerald-500 text-white"
                                                    : isWarningItem
                                                        ? "border-rose-300 hover:border-rose-400"
                                                        : "border-slate-200 hover:border-slate-300"
                                            )}>
                                                {isChecked && (
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </div>

                                            {/* Item Text */}
                                            <span className={cn(
                                                "flex-1 text-sm md:text-base transition-colors",
                                                isChecked 
                                                    ? "text-slate-400 line-through" 
                                                    : isWarningItem
                                                        ? "text-rose-700 font-medium"
                                                        : "text-slate-700"
                                            )}>
                                                {language === 'en' ? item.text_en : item.text}
                                            </span>

                                            {/* Warning indicator */}
                                            {isWarningItem && !isChecked && (
                                                <span className="text-xs text-rose-500 font-medium bg-rose-50 px-2 py-1 rounded-full">
                                                    ⚠️
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Reminder Message */}
            {showReminder && !isComplete && (
                <div 
                    className="mt-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl animate-in fade-in slide-in-from-top-2"
                    data-testid="reminder-message"
                >
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-medium text-amber-800">
                            {language === 'id' 
                                ? 'Post-trade review belum lengkap.'
                                : 'Post-trade review is not complete.'
                            }
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                            {language === 'id'
                                ? 'Selesaikan review sebelum memulakan sesi trade baru.'
                                : 'Complete the review before starting a new trading session.'
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Success Message */}
            {isComplete && (
                <div 
                    className="mt-6 flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-xl animate-in fade-in slide-in-from-top-2"
                    data-testid="success-message"
                >
                    <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-medium text-purple-800">
                            {language === 'id'
                                ? 'Post-trade review lengkap!'
                                : 'Post-trade review complete!'
                            }
                        </p>
                        <p className="text-sm text-purple-700 mt-1">
                            {language === 'id'
                                ? 'Disiplin hari ini menentukan konsistensi jangka panjang.'
                                : 'Today\'s discipline determines long-term consistency.'
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                    variant="outline"
                    onClick={handleReset}
                    className="w-full sm:w-auto rounded-xl px-6"
                    data-testid="reset-btn"
                >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {language === 'id' ? 'Reset Review' : 'Reset Review'}
                </Button>
                
                <Button
                    onClick={handleProceed}
                    disabled={dailyLossTriggered}
                    className={cn(
                        "w-full sm:w-auto rounded-xl px-8",
                        dailyLossTriggered
                            ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed"
                            : isComplete 
                                ? "bg-purple-600 hover:bg-purple-700" 
                                : "bg-primary hover:bg-primary/90"
                    )}
                    data-testid="proceed-btn"
                >
                    {dailyLossTriggered ? (
                        <>
                            <Lock className="w-4 h-4 mr-2" />
                            {language === 'id' ? 'Sesi Ditutup' : 'Session Closed'}
                        </>
                    ) : isComplete ? (
                        <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            {language === 'id' ? 'Review Selesai' : 'Review Complete'}
                        </>
                    ) : (
                        language === 'id' ? 'Semak Review' : 'Check Review'
                    )}
                </Button>
            </div>

            {/* Wisdom Quote */}
            <div className="mt-8 text-center">
                <p className="text-sm text-slate-400 italic">
                    &ldquo;{language === 'id' 
                        ? 'Trader yang berjaya belajar dari setiap trade, bukan hanya yang profit.'
                        : 'Successful traders learn from every trade, not just profitable ones.'
                    }&rdquo;
                </p>
            </div>
        </div>
    );
};
